import SharedPreferences from "../../../constants/SharedPreferences";
import BayunError from "../../../constants/BayunError";
import { getFromStorage, logApiResponse, saveInStorage } from "../../../util/Utility";
import { verifyAndPersistOrgSettingsFromLastUpdatedAt } from "../../../util/SignatureUtil";
import { decryptAndSaveMemberOrgSettingsFields } from "./memberInfoCrypto";

const refreshInFlight = new Map();

export const isOrgSettingsUpdateHeader = (headerValue) =>
  headerValue === true || headerValue === "true";

export async function checkAndRefreshOrgSettings(
  sessionId,
  axiosInstance,
  refreshUrl = "/refreshOrgSettings",
) {
  const orgName = await getFromStorage(sessionId, SharedPreferences.ORG_NAME);
  if (!orgName) {
    return;
  }

  if (refreshInFlight.has(sessionId)) {
    return refreshInFlight.get(sessionId);
  }

  const refreshPromise = refreshAndSaveOrgSettings(
    sessionId,
    axiosInstance,
    refreshUrl,
  );
  refreshInFlight.set(sessionId, refreshPromise);

  try {
    await refreshPromise;
  } finally {
    refreshInFlight.delete(sessionId);
  }
}

async function refreshAndSaveOrgSettings(sessionId, axiosInstance, refreshUrl) {
  const response = await axiosInstance.get(refreshUrl);
  await logApiResponse("refreshOrgSettingsResponse", response);
  await decryptAndSaveRefreshedOrgSettings(
    sessionId,
    await getFromStorage(sessionId, SharedPreferences.ORG_NAME),
    response.data,
  );
}

export async function decryptAndSaveRefreshedOrgSettings(sessionId, orgName, data) {
  if (!data) {
    return;
  }

  // Verify BEFORE persisting anything. keyValidationPolicy selects which trust anchor is
  // acceptable for member public keys, so writing it first and validating afterwards left a
  // window where a tampered policy was already live -- and the signature failure used to be
  // swallowed, so the downgrade became permanent.
  const authenticatedPlaintextSettings = {
    keyValidationPolicy: data.keyValidationPolicy,
    memberApproval: data.memberApproval,
    appApproval: data.appApproval,
  };

  if (data.signedSettings && data.orgSettingsLastUpdatedAt != null) {
    const adminPublicKey = await getFromStorage(
      sessionId,
      SharedPreferences.ADMIN_PUBLIC_KEY,
    );

    await verifyAndPersistOrgSettingsFromLastUpdatedAt(
      data.signedSettings,
      data.orgSettingsLastUpdatedAt,
      sessionId,
      adminPublicKey,
      orgName,
      authenticatedPlaintextSettings,
    );
  } else if (data.keyValidationPolicy) {
    // A policy value with no signature to bind it cannot be trusted.
    throw BayunError.ERROR_ORG_SETTINGS_SIGNATURE_INVALID;
  }

  await decryptAndSaveMemberOrgSettingsFields(sessionId, orgName, data);

  if (data.keyValidationPolicy) {
    await saveInStorage(
      sessionId,
      SharedPreferences.KEY_VALIDATION_POLICY,
      data.keyValidationPolicy,
    );
  }
}
